/**
 * Unit conversion — pure math.
 *
 * Linear units convert through a base unit via a `factor` (how many base units
 * one unit equals): result = value * fromFactor / toFactor. Temperature is
 * non-linear (offsets) and handled separately. All functions return `null` on
 * invalid input so the UI can show a hint instead of NaN.
 */

function finite(n: number): boolean {
  return typeof n === "number" && Number.isFinite(n);
}

/** Convert between two linear units given their base-unit factors. */
export function convertLinear(
  value: number,
  fromFactor: number,
  toFactor: number,
): number | null {
  if (!finite(value) || !finite(fromFactor) || !finite(toFactor)) return null;
  if (toFactor === 0) return null;
  return (value * fromFactor) / toFactor;
}

export type TempUnit = "c" | "f" | "k";

/** Convert temperature between Celsius, Fahrenheit and Kelvin. */
export function convertTemperature(
  value: number,
  from: TempUnit,
  to: TempUnit,
): number | null {
  if (!finite(value)) return null;
  let c: number;
  if (from === "c") c = value;
  else if (from === "f") c = ((value - 32) * 5) / 9;
  else if (from === "k") c = value - 273.15;
  else return null;

  if (to === "c") return c;
  if (to === "f") return (c * 9) / 5 + 32;
  if (to === "k") return c + 273.15;
  return null;
}

/**
 * Format a converted value for display: trims floating-point noise, keeps a
 * sensible number of significant digits, and groups thousands. Returns "" for
 * null.
 */
export function formatResult(value: number | null, locale = "en"): string {
  if (value === null || !Number.isFinite(value)) return "";
  // Round to ~10 significant digits to kill FP noise, then drop trailing zeros.
  const rounded = Number(value.toPrecision(10));
  const abs = Math.abs(rounded);
  const maxFrac = abs !== 0 && abs < 1 ? 8 : 6;
  return rounded.toLocaleString(locale, { maximumFractionDigits: maxFrac });
}
